import type { AdvancedFilterDefinition, JsonLogicRule } from '@/shared/components/filters';
import { ListType } from '@/types';

import { apiClient } from '../http';

export enum SavedFilterResourceType {
    AUDIT_LOG = 'audit-log',
    DASHBOARD = 'dashboard',
    FOLDER = 'folder',
    GLOBAL_TRADE = 'global-trade',
    LIFECYCLE = 'lifecycle',
    LIST = 'list',
    PIPELINE = 'pipeline',
    REPORT = 'report',
    TASK = 'task',
}

export enum SavedFilterScope {
    RESOURCE = 'resource',
    WORKSPACE = 'workspace',
}

export enum SavedFilterVisibility {
    PERSONAL = 'personal',
    WORKSPACE = 'workspace',
}

export type SavedFilterDefinition<TRule = unknown, TConfiguration = unknown> = {
    advancedFilter?: AdvancedFilterDefinition<TRule>;
    advancedFilters?: AdvancedFilterDefinition<TRule>[];
    columns?: { dataType?: string; key: string; label: string }[];
    configuration?: TConfiguration;
    jsonLogic?: JsonLogicRule;
    rules: TRule[];
};

export type SavedFilter<TRule = unknown, TConfiguration = unknown> = {
    canChangeVisibility: boolean;
    canEdit: boolean;
    compatibility?: {
        applicable: boolean;
        disabledReason?: string | null;
    };
    createdAt: Date;
    definition: SavedFilterDefinition<TRule, TConfiguration>;
    description?: string | null;
    id: string;
    isResourceDefault: boolean;
    name: string;
    resourceId?: string | null;
    resourceType: SavedFilterResourceType;
    scope: SavedFilterScope;
    targetKind?: ListType | null;
    updatedAt: Date;
    visibility: SavedFilterVisibility;
};

export type SavedFilterListParams = {
    resourceId: string;
    resourceType: SavedFilterResourceType;
    targetKind?: ListType;
};

export type SavedFilterInput<TRule = unknown, TConfiguration = unknown> = Pick<
    SavedFilter<TRule, TConfiguration>,
    'definition' | 'description' | 'isResourceDefault' | 'name' | 'resourceType' | 'scope' | 'targetKind' | 'visibility'
> & {
    resourceId: string;
};

export type ResourceFilterStateSource = 'none' | 'personal' | 'resource-default';

export type ResourceFilterState<TRule = unknown> = {
    definition: SavedFilterDefinition<TRule>;
    savedFilterId?: string | null;
    source: ResourceFilterStateSource;
};

export type ResourceFilterStateParams = Pick<SavedFilterListParams, 'resourceId' | 'resourceType'>;

export type ResourceFilterStateInput<TRule = unknown> = ResourceFilterStateParams & {
    definition: SavedFilterDefinition<TRule>;
    savedFilterId?: string | null;
};

export const savedFilterService = {
    create: <TRule = unknown, TConfiguration = unknown>(input: SavedFilterInput<TRule, TConfiguration>) =>
        apiClient.post<SavedFilter<TRule, TConfiguration>, SavedFilterInput<TRule, TConfiguration>>(
            '/api/saved-filters',
            input,
        ),
    delete: (id: string) => apiClient.delete<{ id: string }>(`/api/saved-filters/${id}`),
    list: <TRule = unknown, TConfiguration = unknown>(params: SavedFilterListParams) =>
        apiClient.get<SavedFilter<TRule, TConfiguration>[], SavedFilterListParams>('/api/saved-filters', { params }),
    resolve: <TRule = unknown>(id: string, params: SavedFilterListParams, signal?: AbortSignal) =>
        apiClient.get<SavedFilter<TRule>, SavedFilterListParams>(`/api/saved-filters/${id}`, {
            params,
            signal,
        }),
    getState: <TRule = unknown>(params: ResourceFilterStateParams, signal?: AbortSignal) =>
        apiClient.get<ResourceFilterState<TRule>, ResourceFilterStateParams>('/api/saved-filters/state', {
            params,
            signal,
        }),
    updateState: <TRule = unknown>(input: ResourceFilterStateInput<TRule>) =>
        apiClient.put<ResourceFilterState<TRule>, ResourceFilterStateInput<TRule>>('/api/saved-filters/state', input),
    update: <TRule = unknown>(id: string, updates: Partial<SavedFilterInput<TRule>>) =>
        apiClient.put<SavedFilter<TRule>, Partial<SavedFilterInput<TRule>>>(`/api/saved-filters/${id}`, updates),
};
